import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { API } from "../../services/api";
import SearchIcon from "@mui/icons-material/Search";
import DirectionsCarIcon from "@mui/icons-material/DirectionsCar";
import BuildIcon from "@mui/icons-material/Build";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import ErrorIcon from "@mui/icons-material/Error";
import SpeedIcon from "@mui/icons-material/Speed";
import LoginIcon from "@mui/icons-material/Login";

export function Patent() {
  const [license, setLicense] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [vehicleData, setVehicleData] = useState(null);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const validateLicense = (input) => {
    if (!input.trim()) {
      setError("Ingrese una patente");
      return false;
    }
    const patternNew = /^[A-Z]{4}\d{2}$/;
    const patternOld = /^[A-Z]{2}\d{4}$/;
    if (
      !patternNew.test(input.toUpperCase()) &&
      !patternOld.test(input.toUpperCase())
    ) {
      setError("Formato inválido (ej: ABCD12 o AB1234)");
      return false;
    }
    setError("");
    return true;
  };

  const handleSearch = async (e, licenseToSearch = null) => {
    if (e) e.preventDefault();

    const searchLicense = licenseToSearch || license;
    if (!validateLicense(searchLicense)) return;

    try {
      setLoading(true);
      setError("");

      const { data: basicData } = await axios.post(
        `${API.DEV}/car/public/license`,
        { license: searchLicense.toUpperCase() },
        { headers: { "Content-Type": "application/json" } }
      );

      if (!basicData || !basicData.license) {
        setError("No se encontró información del vehículo con esta patente");
        setVehicleData(null);
        return;
      }

      let data = basicData;
      if (basicData.kms) {
        const { data: enrichedData } = await axios.post(
          `${API.DEV}/car/public/license`,
          {
            license: searchLicense.toUpperCase(),
            current_km: basicData.kms,
          },
          { headers: { "Content-Type": "application/json" } }
        );
        data = enrichedData;
      }

      setVehicleData(data);
      setError("");
    } catch (err) {
      console.error("Error al buscar vehículo:", err);

      if (err.response) {
        switch (err.response.status) {
          case 404:
            setError(
              "Vehículo no encontrado. Verifica que la patente esté registrada en el sistema."
            );
            break;
          case 400:
            setError(
              "Formato de patente inválido. Usa el formato correcto (ej: ABCD12 o AB1234)."
            );
            break;
          case 500:
            setError(
              "Error del servidor. Por favor, intenta nuevamente más tarde."
            );
            break;
          default:
            setError(
              "No se pudo consultar el vehículo. Verifica tu conexión e intenta nuevamente."
            );
        }
      } else if (err.request) {
        setError(
          "No se pudo conectar con el servidor. Verifica tu conexión a internet."
        );
      } else {
        setError("Ocurrió un error inesperado. Por favor, intenta nuevamente.");
      }

      setVehicleData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const licenseParam = searchParams.get("license");
    if (licenseParam) {
      setLicense(licenseParam.toUpperCase());
      handleSearch(null, licenseParam);
    }
  }, [searchParams]);

  const handleWhatsApp = () => {
    if (!vehicleData) return;

    const displayKm = vehicleData.kms
      ? vehicleData.kms.toLocaleString("es-CL")
      : "No registrado";

    const nextMaintenance = vehicleData.next_maintenance_km
      ? `Próxima mantención: ${vehicleData.next_maintenance_km.toLocaleString(
          "es-CL"
        )} km`
      : "Próxima mantención: Por definir";

    const requiredServices =
      vehicleData.maintenance_services
        ?.filter((s) => s.status === "REQUERIDO")
        .map((s) => `- ${s.title}`) || [];

    const servicesText =
      requiredServices.length > 0
        ? `\n\n⚠️ *Servicios Requeridos:*\n${requiredServices.join("\n")}`
        : "";

    const message = encodeURIComponent(
      `🚗 *Información del Vehículo*\n\n` +
        `Vehículo: ${vehicleData.brand} ${vehicleData.model}\n` +
        `Patente: ${vehicleData.license}\n` +
        `Kilometraje actual: ${displayKm} km\n` +
        `${nextMaintenance}${servicesText}\n\n` +
        `📋 Ver estado completo: ${window.location.origin}/?license=${vehicleData.license}`
    );
    window.open(`https://wa.me/?text=${message}`, "_blank");
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900">
      <header className="bg-gray-900/50 backdrop-blur-sm border-b border-gray-800">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-blue-600 flex items-center justify-center">
              <DirectionsCarIcon className="text-white" fontSize="small" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-white">GaragePro</h1>
              <p className="text-xs text-gray-400">Consulta tu vehículo</p>
            </div>
          </div>
          <button
            onClick={() => navigate("/login")}
            className="px-4 py-2 bg-gray-800 hover:bg-gray-700 text-gray-300 hover:text-white rounded-lg transition-all flex items-center gap-2 text-sm font-medium border border-gray-700"
          >
            <LoginIcon fontSize="small" />
            Iniciar Sesión
          </button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-12">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-500/10 border border-blue-500/20 rounded-full mb-4">
            <BuildIcon className="text-blue-400" fontSize="small" />
            <span className="text-blue-400 text-sm font-medium">
              Sistema de Mantenciones
            </span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Consulta el estado de tu vehículo
          </h2>
          <p className="text-gray-400 text-lg max-w-2xl mx-auto">
            Ingresa tu patente para ver las próximas mantenciones programadas
          </p>
        </div>

        <div className="bg-gray-800/50 backdrop-blur-sm rounded-2xl border border-gray-700 p-8 mb-8 shadow-2xl">
          <form onSubmit={handleSearch} className="space-y-4">
            <div className="relative">
              <label className="block text-sm font-medium text-gray-400 mb-2">
                Patente del vehículo *
              </label>
              <div className="absolute left-4 top-[42px] pointer-events-none">
                <DirectionsCarIcon className="text-gray-500" />
              </div>
              <input
                type="text"
                value={license}
                onChange={(e) => {
                  setLicense(e.target.value.toUpperCase());
                  if (error) setError("");
                }}
                placeholder="Ej: ABCD12 o AB1234"
                className={`w-full pl-14 pr-4 py-4 bg-gray-900 border ${
                  error ? "border-red-500" : "border-gray-700"
                } rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 ${
                  error ? "focus:ring-red-500" : "focus:ring-blue-500"
                } focus:border-transparent transition-all text-lg`}
                disabled={loading}
              />
              {error && (
                <div className="absolute right-4 top-[42px] pointer-events-none">
                  <ErrorIcon className="text-red-400" />
                </div>
              )}
            </div>

            {error && (
              <div className="flex items-center gap-2 text-red-400 text-sm bg-red-500/10 px-4 py-3 rounded-lg border border-red-500/20">
                <ErrorIcon fontSize="small" />
                <span>{error}</span>
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full px-6 py-4 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-semibold rounded-xl transition-all duration-200 hover:shadow-lg hover:shadow-blue-500/30 flex items-center justify-center gap-3 disabled:opacity-50 disabled:cursor-not-allowed text-lg"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>
                  Buscando...
                </>
              ) : (
                <>
                  <SearchIcon />
                  Buscar Vehículo
                </>
              )}
            </button>
          </form>
        </div>

        {vehicleData && (
          <div className="space-y-6 animate-fadeIn">
            <div className="bg-gray-800/50 backdrop-blur-sm rounded-2xl border border-gray-700 overflow-hidden">
              <div className="px-6 py-4 bg-gradient-to-r from-blue-600/20 to-blue-700/20 border-b border-gray-700 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-600 rounded-lg">
                    <DirectionsCarIcon className="text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-white">
                      {vehicleData.brand} {vehicleData.model}
                    </h3>
                    <p className="text-sm text-gray-400">
                      Patente: {vehicleData.license}
                    </p>
                  </div>
                </div>
                <span className="px-3 py-1 text-xs font-semibold rounded-full bg-green-500/10 text-green-400 border border-green-500/20">
                  Activo
                </span>
              </div>

              <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex items-center gap-3 p-4 bg-gray-900/50 rounded-lg border border-gray-700">
                  <SpeedIcon className="text-blue-400" />
                  <div>
                    <p className="text-xs text-gray-400">Kilometraje Actual</p>
                    <p className="text-lg font-semibold text-white">
                      {vehicleData.kms
                        ? vehicleData.kms.toLocaleString("es-CL")
                        : "No registrado"}{" "}
                      km
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-4 bg-gradient-to-br from-orange-500/10 to-orange-600/10 rounded-lg border border-orange-500/30">
                  <BuildIcon className="text-orange-400" />
                  <div>
                    <p className="text-xs text-orange-400">
                      Próxima Mantención
                    </p>
                    <p className="text-lg font-semibold text-white">
                      {vehicleData.next_maintenance_km
                        ? `${vehicleData.next_maintenance_km.toLocaleString(
                            "es-CL"
                          )} km`
                        : "Por definir"}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {vehicleData.maintenance_summary &&
              vehicleData.maintenance_summary.required > 0 && (
                <div className="bg-red-500/10 border border-red-500/30 rounded-2xl p-6 mb-6">
                  <div className="flex items-center gap-4">
                    <div className="p-3 bg-red-500/20 rounded-full">
                      <ErrorIcon className="text-red-400 text-3xl" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-xl font-bold text-red-400 mb-1">
                        ⚠️ Mantenciones Requeridas
                      </h3>
                      <p className="text-red-300">
                        {vehicleData.maintenance_summary.required} servicio
                        {vehicleData.maintenance_summary.required > 1
                          ? "s"
                          : ""}{" "}
                        requiere
                        {vehicleData.maintenance_summary.required > 1
                          ? "n"
                          : ""}{" "}
                        atención inmediata
                      </p>
                    </div>
                    <div className="text-center px-6 py-3 bg-red-500/20 rounded-lg border border-red-500/40">
                      <div className="text-3xl font-bold text-red-300">
                        {vehicleData.maintenance_summary.required}
                      </div>
                      <div className="text-xs text-red-400 font-semibold">
                        URGENTE
                      </div>
                    </div>
                  </div>
                </div>
              )}

            <div className="bg-gray-800/50 backdrop-blur-sm rounded-2xl border border-gray-700 overflow-hidden">
              <div className="px-6 py-4 bg-gradient-to-r from-orange-600/20 to-orange-700/20 border-b border-gray-700">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-orange-600 rounded-lg">
                      <BuildIcon className="text-white" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-white">
                        Estado de Mantenciones
                      </h3>
                      <p className="text-sm text-gray-400">
                        {vehicleData.maintenance_services?.length || 0}{" "}
                        servicios programados
                      </p>
                    </div>
                  </div>
                  {vehicleData.maintenance_summary && (
                    <div className="flex gap-3">
                      {vehicleData.maintenance_summary.required > 0 && (
                        <div className="text-center">
                          <div className="text-2xl font-bold text-red-400">
                            {vehicleData.maintenance_summary.required}
                          </div>
                          <div className="text-xs text-red-400">Requeridos</div>
                        </div>
                      )}
                      {vehicleData.maintenance_summary.upcoming > 0 && (
                        <div className="text-center">
                          <div className="text-2xl font-bold text-yellow-400">
                            {vehicleData.maintenance_summary.upcoming}
                          </div>
                          <div className="text-xs text-yellow-400">
                            Próximos
                          </div>
                        </div>
                      )}
                      {vehicleData.maintenance_summary.upToDate > 0 && (
                        <div className="text-center">
                          <div className="text-2xl font-bold text-green-400">
                            {vehicleData.maintenance_summary.upToDate}
                          </div>
                          <div className="text-xs text-green-400">Al día</div>
                        </div>
                      )}
                      {vehicleData.maintenance_summary.noData > 0 && (
                        <div className="text-center">
                          <div className="text-2xl font-bold text-gray-400">
                            {vehicleData.maintenance_summary.noData}
                          </div>
                          <div className="text-xs text-gray-400">Sin datos</div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>

              <div className="p-6 space-y-3">
                {vehicleData.maintenance_services &&
                vehicleData.maintenance_services.length > 0 ? (
                  vehicleData.maintenance_services.map((service, index) => (
                    <MaintenanceItem
                      key={index}
                      icon={BuildIcon}
                      title={service.title}
                      description={
                        service.description ||
                        `Intervalo: cada ${service.interval}`
                      }
                      status={service.status}
                      priority={service.priority}
                      nextServiceKm={service.nextServiceNum}
                      interval={service.interval}
                    />
                  ))
                ) : vehicleData.services && vehicleData.services.length > 0 ? (
                  vehicleData.services.map((service, index) => (
                    <MaintenanceItem
                      key={index}
                      icon={BuildIcon}
                      title={service.title}
                      description={
                        service.description ||
                        `Intervalo: cada ${service.interval}`
                      }
                      status="AL_DIA"
                      priority="Normal"
                      nextServiceKm={service.nextServiceNum}
                      interval={service.interval}
                    />
                  ))
                ) : (
                  <div className="text-center py-8 text-gray-400">
                    <BuildIcon
                      className="mx-auto mb-3 text-gray-600"
                      fontSize="large"
                    />
                    <p>No hay servicios configurados para este vehículo</p>
                  </div>
                )}
              </div>

              <div className="p-6 border-t border-gray-700 bg-gray-900/30">
                <button
                  onClick={handleWhatsApp}
                  className="w-full px-6 py-4 bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white font-semibold rounded-xl transition-all duration-200 hover:shadow-lg hover:shadow-green-500/30 flex items-center justify-center gap-3 text-lg"
                >
                  <WhatsAppIcon />
                  Enviar por WhatsApp
                </button>
                <p className="text-center text-xs text-gray-500 mt-3">
                  Comparte esta información con tu mecánico
                </p>
              </div>
            </div>
          </div>
        )}
      </main>

      <footer className="mt-20 border-t border-gray-800 bg-gray-900/50 backdrop-blur-sm">
        <div className="max-w-6xl mx-auto px-4 py-8 text-center text-gray-400 text-sm">
          <p>© 2024 GaragePro - Sistema de Gestión de Taller Mecánico</p>
          <p className="mt-2 text-xs text-gray-500">
            Desarrollado por MOCCA Labs
          </p>
        </div>
      </footer>
    </div>
  );
}

const MaintenanceItem = ({
  icon: Icon,
  title,
  description,
  status,
  priority,
  nextServiceKm,
  interval,
}) => {
  const statusConfig = {
    REQUERIDO: {
      color: "bg-red-500/20 text-red-300 border-red-500/40",
      label: "REQUERIDO",
      iconColor: "text-red-400",
      bgColor: "bg-red-500/10 border-red-500/40",
      priorityColor: "text-red-300",
    },
    PROXIMO: {
      color: "bg-yellow-500/20 text-yellow-300 border-yellow-500/40",
      label: "PRÓXIMO",
      iconColor: "text-yellow-400",
      bgColor: "bg-yellow-500/10 border-yellow-500/40",
      priorityColor: "text-yellow-300",
    },
    AL_DIA: {
      color: "bg-green-500/20 text-green-300 border-green-500/40",
      label: "AL DÍA",
      iconColor: "text-green-400",
      bgColor: "bg-green-500/5 border-green-500/30",
      priorityColor: "text-green-300",
    },
    SIN_DATOS: {
      color: "bg-gray-500/20 text-gray-300 border-gray-500/40",
      label: "SIN DATOS",
      iconColor: "text-gray-400",
      bgColor: "bg-gray-500/5 border-gray-600/30",
      priorityColor: "text-gray-400",
    },
    Próximo: {
      color: "bg-yellow-500/20 text-yellow-300 border-yellow-500/40",
      label: "PRÓXIMO",
      iconColor: "text-yellow-400",
      bgColor: "bg-yellow-500/10 border-yellow-500/40",
      priorityColor: "text-yellow-300",
    },
    Pendiente: {
      color: "bg-orange-500/20 text-orange-300 border-orange-500/40",
      label: "PENDIENTE",
      iconColor: "text-orange-400",
      bgColor: "bg-orange-500/10 border-orange-500/40",
      priorityColor: "text-orange-300",
    },
    Programado: {
      color: "bg-blue-500/20 text-blue-300 border-blue-500/40",
      label: "PROGRAMADO",
      iconColor: "text-blue-400",
      bgColor: "bg-blue-500/10 border-blue-500/40",
      priorityColor: "text-blue-300",
    },
  };

  const config = statusConfig[status] || statusConfig.AL_DIA;

  const hasNoData =
    status === "SIN_DATOS" ||
    priority === "-" ||
    (!nextServiceKm && priority === "Normal");

  return (
    <div
      className={`flex items-center gap-4 p-4 rounded-lg border ${config.bgColor} hover:border-gray-600 transition-all`}
    >
      <div className={`p-3 bg-gray-800 rounded-lg border border-gray-700`}>
        <Icon className={config.iconColor} />
      </div>
      <div className="flex-1">
        <h4 className="text-white font-semibold mb-1">{title}</h4>
        <p className="text-sm text-gray-400">{description}</p>
        {interval && (
          <p className="text-xs text-gray-500 mt-1">
            Intervalo: cada {interval}
          </p>
        )}
        {nextServiceKm && nextServiceKm > 0 && (
          <p className="text-xs text-blue-400 mt-1 font-medium">
            Próximo servicio: {nextServiceKm.toLocaleString("es-CL")} km
          </p>
        )}
      </div>
      <div className="text-right min-w-[120px]">
        <div className="flex flex-col gap-2">
          <span
            className={`px-3 py-1.5 text-xs font-bold rounded-lg border ${config.color} inline-flex items-center justify-center`}
          >
            {config.label}
          </span>
          {priority &&
            priority !== "-" &&
            priority !== "Normal" &&
            !hasNoData && (
              <span className={`text-xs font-semibold ${config.priorityColor}`}>
                {priority}
              </span>
            )}
        </div>
      </div>
    </div>
  );
};
